import React from 'react';
import Points from 'components/Points';
import { AssetUrls } from 'config/assets';
import { useIsMobile } from 'hooks/useIsMobile';
import { PanelDirection, PanelStatus } from './types';
import './TradeTabStrip.css';

export interface TabFigure {
  count: number;
  /** Omitted when the backend predates stats.byStatus */
  points?: number;
}

interface Props {
  direction: PanelDirection;
  activeStatus: PanelStatus;
  onChange: (status: PanelStatus) => void;
  /** Absent while loading: the tabs render without badges */
  figures?: Record<PanelStatus, TabFigure>;
  /** Right-aligned slot: the overdue count and the Expand control while the panel is collapsed */
  trailing?: React.ReactNode;
}

const TABS: Array<{ status: PanelStatus; icon: string }> = [
  { status: 'ready', icon: AssetUrls.icons.misc.sendPackage },
  { status: 'transit', icon: `${AssetUrls.base}/assets/icons/SendCards/truck-in-transit.svg` },
  { status: 'completed', icon: `${AssetUrls.base}/assets/icons/SendCards/icon-completed.svg` },
  { status: 'cancelled', icon: `${AssetUrls.base}/assets/icons/cancelled.svg` },
];

const label = (status: PanelStatus, direction: PanelDirection, isMobile: boolean): string => {
  switch (status) {
    case 'ready':
      if (isMobile) return direction === 'inbound' ? 'Preparing' : 'Ready';
      return direction === 'inbound' ? 'Preparing to ship' : 'Ready to ship';
    case 'transit':
      return isMobile ? 'Transit' : 'In Transit';
    case 'completed':
      return isMobile ? 'Done' : 'Completed';
    default:
      return 'Cancelled';
  }
};

/**
 * The tab row for /sending and /receiving. Replaces the two per-page tab
 * components, whose `.status-tab` rules were duplicated across two global
 * sheets; everything here is `ttp-` prefixed so it cannot leak.
 */
const TradeTabStrip: React.FC<Props> = ({ direction, activeStatus, onChange, figures, trailing }) => {
  const isMobile = useIsMobile();

  return (
    <div className="ttp-strip-wrap">
      {/* A real horizontal scroller on mobile. Never overflow: hidden: all four
          tabs must stay reachable, and the fade marks that there is more. */}
      <div className="ttp-strip" role="tablist" data-testid="ttp-strip">
        {TABS.map(({ status, icon }) => {
          const figure = figures?.[status];
          const isActive = activeStatus === status;
          return (
            <button
              key={status}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`ttp-tab ${isActive ? 'ttp-tab--active' : ''}`}
              data-testid={`ttp-tab-${status}`}
              onClick={() => onChange(status)}
            >
              <img src={icon} alt="" className="ttp-tab-icon" />
              <span className="ttp-tab-label">{label(status, direction, isMobile)}</span>
              {figure && (
                <span className="ttp-tab-badge" data-testid={`ttp-tab-badge-${status}`}>
                  {figure.count.toLocaleString('en-US')}
                </span>
              )}
              {!isMobile && figure?.points !== undefined && figure.points > 0 && (
                <Points points={figure.points} type="small" />
              )}
            </button>
          );
        })}
      </div>
      <div className="ttp-strip-fade" aria-hidden="true" />
      {trailing && <div className="ttp-strip-trailing">{trailing}</div>}
    </div>
  );
};

export default TradeTabStrip;
